'use strict';
import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import BreadcrumbComponent from '@src/views/item/breadcrumb';
import { cn } from '@src/utils/ui';
import { ImageGallery } from '@src/components/ui/image-gallery/image-gallery';
import { InfoSection } from './info-section';
import { ContactSeller } from './contact-seller/contact-seller';
import { SellerInfo } from './seller-info';
import { ServiceDetailPropType } from './prop-types/service-detail';
import { LoggedInUserPropType } from '@src/components/prop-types/user';

import validatePermissionToPerformAction from '../../../../../redux/accountPermissionActions';
import QuotationActions from '../../../../../redux/quotationActions';
import ItemDetailActions from '../../../../../redux/itemdetailsActions';
import WishlistActions from '../../../../../redux/wishlistActions';
import ChatActions from '../../../../../redux/chatActions';
import CartActions from '../../../../../redux/cartActions';
import userActions from '../../../../../redux/userActions';
import actionTypes from '../../../../../redux/actionTypes';
import { Container } from '@mui/material';
import { Reviews } from './reviews';
import { get } from 'lodash';

if (typeof window !== 'undefined') {
    var $ = window.$;
}

export const getServiceMainInfo = service => {
    return {
        name: service.name,
        description: service.description,
        price: service.price,
        priceUnit: service.priceUnit,
        location: service.location,
        businessType: service.seller.businessTypeLabel,
        badges: service.badges,
        ratings: service.feedback.ratings,
        ratingsCount: service.feedback.ratingsCount,
        openingHours: service.seller.openingHours,
        blockedDates: service.seller.blockedDates,
        allDay: service.seller.allDay,
        verifications: service.seller.verifications
    };
};

const ItemDetailMainComponent = ({ itemDetails, categories, user, ...props }) => {
    const serviceMainInfo = getServiceMainInfo(itemDetails);

    const baseUrl = process.env.SITE_URL;
    const slugName = itemDetails.name ? itemDetails.name.replace(/\s+/g, '').replace(/[^\w-]/g, '') : 'listing';
    const pathname = typeof window !== 'undefined' ? window.location.pathname : `/items/${slugName}/${itemDetails.id}`;
    const url = `${baseUrl}${pathname}`;
    const title = `${itemDetails.name} | BCAA Task Marketplace`;
    const description =
        itemDetails.description || `Find trusted ${itemDetails.categoryName || 'services'} on BCAA Task Marketplace.`;
    const image = itemDetails.images?.[0]?.mediaUrl;

    return (
        <Container maxWidth='xl' className='tw-pb-[15px]'>
            <Helmet>
                <title>{title}</title>
                <link rel='canonical' href={url} />
                <meta name='description' content={description} />
                <meta name='robots' content='index, follow' />

                {/* Open Graph Meta */}
                <meta property='og:title' content={title} />
                <meta property='og:description' content={description} />
                <meta property='og:image' content={image} />
                <meta property='og:url' content={url} />
                <meta property='og:site_name' content='BCAA Task Marketplace' />
                <meta property='og:type' content='website' />
            </Helmet>

            <BreadcrumbComponent categories={categories} />
            <div className='main-section tw-w-full tw-grid tw-grid-cols-1 lg:tw-grid-cols-12 tw-gap-x-[16px] lg:tw-gap-x-[48px] tw-gap-y-[24px] tw-pt-[15px]'>
                {/* Details section */}
                <div className={cn('details-section', 'tw-col-span-1 lg:tw-col-span-8 tw-space-y-[16px]')}>
                    <div className='photos-section'>
                        <ImageGallery itemDetails={itemDetails} user={user} {...props} />
                    </div>
                    <div className='info-section'>
                        <InfoSection
                            serviceMainInfo={serviceMainInfo}
                            itemDetails={itemDetails}
                            user={user}
                            {...props}
                        />
                    </div>
                </div>

                {/* Seller integration beside details on desktop+ */}
                <div className={cn('seller_integration-section', 'tw-col-span-1 lg:tw-col-span-4 tw-space-y-[16px]')}>
                    <ContactSeller user={user} itemDetails={itemDetails} {...props} />
                    <SellerInfo {...itemDetails.seller} user={user} {...props} />
                </div>

                <div className='tw-col-span-1 lg:tw-col-span-8 tw-space-y-[16px]'>
                    <Reviews feedback={itemDetails.feedback} />
                </div>
            </div>
            <div className='tw-text-[10px] tw-text-[#5A5A5A] tw-space-y-[16px] tw-py-[8px] tw-pb-[25px] tw-pt-[20px]'>
                <p>
                    *Insurance coverage is subject to the Tasker Insurance policy terms and conditions, including eligibility requirements, 
                    limitations, and exclusions. Tasker Insurance is a group insurance policy underwritten by BCAA Insurance Corporation and 
                    issued to B.C.A.A. Holdings Ltd. BCAA Insurance Corporation is a subsidiary of B.C.A.A. Holdings Ltd. 
                </p>
                <p>
                    BCAA Task Marketplace is a public, peer-to-peer marketplace that connects users with independent
                    service providers. BCAA does not endorse, verify, or warrant the accuracy, quality, suitability, or
                    legality of any service listings, and disclaims all liability arising from or related to such
                    listings. Users are solely responsible for communicating directly with providers to obtain
                    additional information, clarify details, and assess the suitability and legitimacy of any services
                    offered.
                </p>
                <p>
                    Any badges or indicators appearing on provider profiles are automatically generated based on
                    third-party tools, as indicated, or publicly available data and are intended for general
                    informational purposes only. Such indicators may not accurately reflect the actual quality,
                    reliability, or experience of a provider. See{' '}
                    <a
                        href='https://www.bcaa.com/legal/Task-Marketplace-terms'
                        target='_blank'
                        className='tw-text-primary-500 hover:tw-font-medium hover:tw-text-primary-500'
                    >
                        Terms of Service
                    </a>{' '}
                    for full terms and conditions, including disclaimers and user acknowledgement of risk.
                </p>
            </div>
        </Container>
    );
};

const CategoryPropType = PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    parentCategoryID: PropTypes.string
});

const CategoriesPropType = PropTypes.arrayOf(CategoryPropType);

ItemDetailMainComponent.propTypes = {
    itemDetails: ServiceDetailPropType,
    categories: CategoriesPropType,
    user: LoggedInUserPropType
};

function mapStateToProps(state) {
    return {
        feedback: state.itemsReducer.feedback,
        itemDetails: state.itemsReducer.items,
        categories: state.itemsReducer.categories,
        message: state.itemsReducer.message,
        paymentTerms: state.merchantReducer.paymentTerms,
        priceValues: state.itemsReducer.priceValues,
        processing: state.itemsReducer.processing,
        ReviewAndRating: state.itemsReducer.ReviewAndRating,
        bookings: state.itemsReducer.bookings,
        user: state.userReducer.user,
        favoriteWishlist: state.itemsReducer.favoriteWishlist,
        controlFlags: state.marketplaceReducer.ControlFlags,
        permissions: state.userReducer.permissions
    };
}

function mapDispatchToProps(dispatch) {
    return {
        contactSeller: () => dispatch({ type: actionTypes.LATEST_ITEMS }),
        addOrEditCart: (cartItemId, quantity, options, successCallback, failedCallback) =>
            dispatch(ItemDetailActions.addOrEditCart(cartItemId, quantity, options, successCallback, failedCallback)),
        getUserCarts: (options, callback) => dispatch(CartActions.getUserCarts(options, callback)),
        deleteCartItem: (cartItemId, userId) => dispatch(CartActions.deleteCartItem(cartItemId, userId)),
        setProcessing: processing => dispatch({ type: actionTypes.PROCESSING, processing: processing }),
        getUserChannels: (options, callback) => dispatch(ChatActions.getUserChannels(options, callback)),
        createChatChannel: (options, callback) => dispatch(ChatActions.createChannel(options, callback)),
        getChatDetails: (channelId, callback) => dispatch(ChatActions.getChatDetails(channelId, callback)),
        updateUserInfo: userInfo => dispatch(userActions.updateUserInfo(userInfo)),
        selectedFeedBack: feedbackId => dispatch(ItemDetailActions.selectedFeedBack(feedbackId)),
        addReplyFeedBack: message => dispatch(ItemDetailActions.addReplyFeedBack(message)),
        updateMessage: message => dispatch(ItemDetailActions.updateMessage(message)),
        //Comparison Widget
        // getUserComparisons: (createIfEmpty, namesOnly, pageSize) =>
        //     dispatch(ComparisonActions.getUserComparisons(createIfEmpty, namesOnly, pageSize)),
        // getComparison: (id, includes) => dispatch(ComparisonActions.getComparison(id, includes)),
        // createComparison: name => dispatch(ComparisonActions.createComparison(name)),
        // editComparison: name => dispatch(ComparisonActions.editComparison(name)),
        // setComparisonToUpdate: id => dispatch(ComparisonActions.setComparisonToUpdate(id)),
        // setComparisonDetailToUpdate: id => dispatch(ComparisonActions.setComparisonDetailToUpdate(id)),
        // deleteComparisonDetail: () => dispatch(ComparisonActions.deleteComparisonDetail()),
        // createComparisonDetail: (cartItemId, includes, comparisonFields) =>
        //     dispatch(ComparisonActions.createComparisonDetail(cartItemId, includes, comparisonFields)),
        // updateComparisonDetail: (cartItemId, quantity, subTotal, discountAmount, addOns) =>
        //     dispatch(ComparisonActions.updateComparisonDetail(cartItemId, quantity, subTotal, discountAmount, addOns)),

        generateInvoiceByCartItem: (cartItemIds, callback) =>
            dispatch(QuotationActions.generateInvoiceByCartItem(cartItemIds, callback)),

        validatePermissionToPerformAction: (code, callback) =>
            dispatch(validatePermissionToPerformAction(code, callback)),

        setFavoriteWishlist: (options, callback) => dispatch(WishlistActions.setFavoriteWishlist(options, callback)),
        delFavoriteWishlist: (options, callback) => dispatch(WishlistActions.delFavoriteWishlist(options, callback))
    };
}

export { mapStateToProps, mapDispatchToProps, ItemDetailMainComponent };

export default {
    mapStateToProps,
    mapDispatchToProps,
    ItemDetailMainComponent
};
